import { useState } from 'react';

import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import type { LWApiPublicDomainListResultRawI } from '@liquidweb-dev/club-sauce/public/domain/index.raw';
import Button from '@liquidweb-dev/undercurrent/Button';
import Chip from '@liquidweb-dev/undercurrent/Chip';
import CalendarClockIcon from '@liquidweb-dev/undercurrent/icons/lucide/CalendarClock';
import ClockAlertIcon from '@liquidweb-dev/undercurrent/icons/lucide/ClockAlert';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import RefreshCwIcon from '@liquidweb-dev/undercurrent/icons/lucide/RefreshCw';
import CloudflareIcon from '@liquidweb-dev/undercurrent/icons/platform/Cloudflare';
import Tooltip from '@liquidweb-dev/undercurrent/Tooltip';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import TypographyOverflow from '@liquidweb-dev/undercurrent/TypographyOverflow';
import dayjs from 'dayjs';

import AssetListItem, {
	AssetListItemDetail,
	AssetListItemDetails,
	AssetListItemMenuArea,
	AssetListItemName,
	AssetListItemStatus,
} from 'components/standard/AssetListItem';
import DialogAddSsl from 'components/standard/DialogAddSsl';
import DnsAttribute from 'components/standard/DnsAttribute';
import SslAttribute from 'components/standard/SslAttribute';
import {
	getDomainStatus,
	getDomainStatusProps,
	getExpirationStatus,
} from 'modules/domains/statuses';
import ActionsMenu from './ActionsMenu';
import ActionsProvider from './ActionsProvider';

type DomainItem = NonNullable<LWApiPublicDomainListResultRawI['items']>[number];

interface DomainRowProps {
	item: DomainItem;
	cdnDomains: Record<string, string>;
}

const formatDate = (date: string): string => {
	return dayjs(date).format('MM/DD/YYYY');
};

const InactiveDomainRow = ({
	item,
	cdnDomains,
}: {
	item: DomainItem;
	cdnDomains: Record<string, string>;
}) => {
	const { Icon, color, primaryInfo } = getDomainStatusProps('expired');
	const { domain, dreg } = item;
	const canRenewDomain = fromApiBoolean(dreg?.renewable);

	return (
		<ActionsProvider
			domain={domain}
			canRenewDomain={canRenewDomain}
			dregUniqId={dreg?.uniq_id}
		>
			<AssetListItem
				to={`/domain/${item.domain}`}
				sx={{
					opacity: 0.5,
				}}
			>
				<AssetListItemStatus
					icon={
						<Icon sx={({ size }) => ({ width: size.xS, height: size.xS })} />
					}
					primaryInfo={primaryInfo}
					color={color}
				/>
				<AssetListItemName
					direction="row"
					sx={{ alignItems: 'center', flex: 1 }}
				>
					<TypographyOverflow variant="label-lg" sx={{ fontWeight: 500 }}>
						{item.domain}
					</TypographyOverflow>
				</AssetListItemName>

				<AssetListItemDetails>
					{item.dreg?.expiration && (
						<AssetListItemDetail>
							<CalendarClockIcon
								sx={({ size }) => ({
									width: size.xS,
									height: size.xS,
								})}
							/>
							<Typography variant="label-md">
								{formatDate(item.dreg.expiration)}
							</Typography>
						</AssetListItemDetail>
					)}
				</AssetListItemDetails>
				<AssetListItemMenuArea>
					<ActionsMenu item={item} cdnDomains={cdnDomains} />
				</AssetListItemMenuArea>
			</AssetListItem>
		</ActionsProvider>
	);
};

const RegularDomainRow = ({
	item,
	cdnDomains,
}: {
	item: DomainItem;
	cdnDomains: Record<string, string>;
}) => {
	const { domain, dns, dreg, ssl } = item;

	const dnsArray = Array.isArray(dns) ? dns : [];
	const sslArray = Array.isArray(ssl) ? ssl : [];
	const hasSsl = sslArray.length > 0;
	const hasWildcardSsl = sslArray.some(({ wildcard }) =>
		fromApiBoolean(wildcard),
	);

	const [isAddSslDialogOpen, setIsAddSslDialogOpen] = useState(false);

	const domainExpirationStatus = getExpirationStatus({
		expiration: dreg?.expiration,
		autorenew: fromApiBoolean(dreg?.autorenew),
	});
	const sslExpirationStatuses = sslArray.map(({ expiration, autorenew }) =>
		getExpirationStatus({
			expiration,
			autorenew: fromApiBoolean(autorenew),
		}),
	);

	const hasExpiringDomain = domainExpirationStatus === 'expiringSoon';
	const isAutoRenewEnabled = fromApiBoolean(dreg?.autorenew);
	const canRenewDomain = fromApiBoolean(dreg?.renewable);
	const hasExpiringSSL = sslExpirationStatuses.some(
		(status) => status === 'expiringSoon',
	);
	const hasExpiring = hasExpiringDomain || hasExpiringSSL;
	const isCloudflareActive = Boolean(cdnDomains[domain]);
	const domainStatus = getDomainStatus(item);
	const isDomainExternal = domainStatus === 'external';
	const { Icon, color, primaryInfo } = getDomainStatusProps(domainStatus);

	return (
		<ActionsProvider
			domain={domain}
			canRenewDomain={canRenewDomain}
			dregUniqId={dreg?.uniq_id}
		>
			<AssetListItem to={`/domain/${domain}`}>
				<AssetListItemStatus
					icon={
						<Icon
							sx={({ size }) => ({
								width: size.xS,
								height: size.xS,
							})}
						/>
					}
					primaryInfo={primaryInfo}
					color={color}
				/>
				<AssetListItemName
					direction="row"
					sx={{ alignItems: 'center', flex: 1 }}
				>
					<TypographyOverflow variant="label-lg" sx={{ fontWeight: 500 }}>
						{domain}
					</TypographyOverflow>
					{hasExpiring && (
						<Chip
							size="small"
							label={hasExpiringDomain ? 'Expires soon' : 'SSL expires soon'}
							color={hasExpiringDomain ? 'danger' : 'warning'}
						/>
					)}
				</AssetListItemName>
				<AssetListItemDetails>
					<Tooltip title="DNS" placement="top">
						<AssetListItemDetail>
							<DnsAttribute dnsZones={dnsArray.length} />
						</AssetListItemDetail>
					</Tooltip>
					{hasSsl ? (
						<Tooltip title="SSL" placement="top">
							<AssetListItemDetail>
								<SslAttribute
									ssls={sslArray.length}
									hasWildcard={hasWildcardSsl}
								/>
							</AssetListItemDetail>
						</Tooltip>
					) : (
						<AssetListItemDetail>
							<Button
								variant="secondary"
								size="small"
								color="highlight"
								startIcon={<PlusIcon />}
								onClick={(event) => {
									event.preventDefault();
									setIsAddSslDialogOpen(true);
								}}
							>
								Add SSL
							</Button>
						</AssetListItemDetail>
					)}
					<Tooltip title="Cloudflare" placement="top">
						<AssetListItemDetail sx={{ opacity: isCloudflareActive ? 1 : 0.5 }}>
							<CloudflareIcon
								sx={({ size }) => ({
									width: size.xS,
									height: size.xS,
								})}
								variant={isCloudflareActive ? 'color' : 'monochromatic'}
							/>
							<Typography
								variant="label-lg"
								sx={{
									...(!isCloudflareActive && {
										color: ({ vars }) => vars.palette.uc.text.main,
									}),
								}}
							>
								{isCloudflareActive ? 'Active' : 'Inactive'}
							</Typography>
						</AssetListItemDetail>
					</Tooltip>
					{dreg?.expiration && (
						<Tooltip title="Expiration date" placement="top">
							<AssetListItemDetail>
								{hasExpiringDomain ? (
									<ClockAlertIcon
										sx={({ size }) => ({
											width: size.xS,
											height: size.xS,
											color: ({ vars }) => vars.palette.uc.danger.text.main,
										})}
									/>
								) : (
									<CalendarClockIcon
										sx={({ size }) => ({
											width: size.xS,
											height: size.xS,
										})}
									/>
								)}
								<Typography
									variant="label-lg"
									sx={{
										...(hasExpiringDomain && {
											color: ({ vars }) => vars.palette.uc.danger.text.main,
										}),
									}}
								>
									{formatDate(dreg.expiration)}
								</Typography>
							</AssetListItemDetail>
						</Tooltip>
					)}
					{!isDomainExternal && (
						<Tooltip title="Autorenew" placement="top">
							<AssetListItemDetail
								sx={{ opacity: isAutoRenewEnabled ? 1 : 0.5 }}
							>
								<RefreshCwIcon
									sx={({ size, vars }) => ({
										width: size.xS,
										height: size.xS,
										...(isAutoRenewEnabled && {
											color: vars.palette.uc.success.text.main,
										}),
									})}
								/>
								<Typography
									variant="label-lg"
									sx={{
										...(isAutoRenewEnabled && {
											color: ({ vars }) => vars.palette.uc.success.text.main,
										}),
									}}
								>
									{
										// TODO: Reword to "Enabling..." or "Disabling..." when loading
										isAutoRenewEnabled ? 'Enabled' : 'Disabled'
									}
								</Typography>
							</AssetListItemDetail>
						</Tooltip>
					)}
				</AssetListItemDetails>

				<AssetListItemMenuArea>
					<ActionsMenu item={item} cdnDomains={cdnDomains} />
				</AssetListItemMenuArea>
			</AssetListItem>

			{!hasSsl && (
				<DialogAddSsl
					onClose={() => setIsAddSslDialogOpen(false)}
					open={isAddSslDialogOpen}
				/>
			)}
		</ActionsProvider>
	);
};

const DomainRow = ({ item, cdnDomains }: DomainRowProps) => {
	const domainStatus = getDomainStatus(item);

	if (domainStatus === 'expired') {
		return <InactiveDomainRow item={item} cdnDomains={cdnDomains} />;
	}

	return <RegularDomainRow item={item} cdnDomains={cdnDomains} />;
};

export default DomainRow;
